/**
 * Phase 18 — admin renders gallery (read-only).
 *
 * Shows the customer's approved renders inline on the admin review
 * page. Tapping opens the parent Lightbox. No approve / reject / delete
 * affordances — those are customer-only.
 */

import { useEffect, useState } from "react";

import { listRenders } from "@starz/shared/api/sdk.gen";
import type { RemodelRender } from "@starz/shared/api/types.gen";

interface Props {
  estimateId: string;
  onOpenLightbox: (url: string, alt: string) => void;
}

export function AdminRendersGallery({ estimateId, onOpenLightbox }: Props) {
  const [renders, setRenders] = useState<RemodelRender[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await listRenders({ estimateId, status: "approved" });
      if (cancelled) return;
      if (!("error" in res)) setRenders(res.data);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [estimateId]);

  if (loading) return null;
  if (renders.length === 0) {
    return (
      <section
        aria-label="Customer-approved renders"
        className="rounded-xl border border-starz-gold/20 bg-white p-5 shadow-sm"
      >
        <h3 className="font-serif text-base font-semibold text-starz-navy">
          Customer-approved renders
        </h3>
        <p className="mt-2 text-sm text-warm-gray">
          The customer hasn't approved any renders yet.
        </p>
      </section>
    );
  }

  return (
    <section
      aria-label="Customer-approved renders"
      className="rounded-xl border border-starz-gold/20 bg-white p-5 shadow-sm"
    >
      <h3 className="font-serif text-base font-semibold text-starz-navy">
        Customer-approved renders ({renders.length})
      </h3>
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
        {renders.map((r) => (
          <button
            key={r.render_id}
            type="button"
            onClick={() =>
              r.url ? onOpenLightbox(r.url, `Render of "${r.prompt}"`) : undefined
            }
            className="flex flex-col gap-1 overflow-hidden rounded-lg border border-starz-gold/20 bg-starz-cream"
            disabled={!r.url}
          >
            {r.url ? (
              <img
                src={r.url}
                alt={`Render of "${r.prompt}"`}
                className="aspect-square w-full object-cover"
              />
            ) : (
              <div className="aspect-square w-full bg-starz-cream/60" />
            )}
            <p className="truncate px-2 py-1 text-[11px] italic text-warm-gray">
              "{r.prompt}"
            </p>
          </button>
        ))}
      </div>
    </section>
  );
}
